/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import type { CashActivityItem } from '../../reports/infra/reportsRemoteRepo';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { settingsStyles as styles } from '../../settings/screens/settingsScreen.styles';
import type { useCashActivityFeed } from './hooks/useCashActivityFeed';
import { CashActivityRow } from './CashActivityList';

type ActivityVm = ReturnType<typeof useCashActivityFeed>;

type Props = {
  vm: ActivityVm;
  operatorLabel: (userId: string | null | undefined) => string | null;
  layoutWide?: boolean;
};

export function CashActivityFeed({ vm, operatorLabel, layoutWide }: Props) {
  useEffect(() => {
    void vm.ensureLoaded();
  }, [vm.ensureLoaded]);

  const renderItem = useCallback(
    ({ item }: { item: CashActivityItem }) => (
      <CashActivityRow item={item} operatorLabel={operatorLabel} layoutWide={layoutWide} />
    ),
    [layoutWide, operatorLabel]
  );

  if (vm.loading && vm.items.length === 0) {
    return (
      <View style={localStyles.emptyWrap}>
        <ActivityIndicator color={ticketColors.gold} />
        <Text style={styles.caption}>Cargando movimientos…</Text>
      </View>
    );
  }

  return (
    <View style={localStyles.root}>
      {vm.partialOffline ? (
        <View style={localStyles.offlineBanner} accessibilityRole="text">
          <Text style={localStyles.offlineText}>
            Vista parcial sin conexión. Ves lo de este equipo.
          </Text>
        </View>
      ) : null}
      {vm.fetchError ? (
        <View style={localStyles.errorBanner} accessibilityRole="alert" testID="caja.activity.error">
          <Text style={[localStyles.offlineText, { flex: 1 }]}>
            No pudimos cargar la actividad. Revisá la conexión.
          </Text>
          <TouchableOpacity
            onPress={() => void vm.reload()}
            accessibilityRole="button"
            accessibilityLabel="Reintentar actividad"
            testID="caja.activity.retry"
          >
            <Text style={localStyles.loadMoreText}>Reintentar</Text>
          </TouchableOpacity>
        </View>
      ) : null}
      <FlatList
        data={vm.items}
        keyExtractor={(item) => `${item.kind}:${item.id}:${item.occurredAt}`}
        renderItem={renderItem}
        scrollEnabled={false}
        ListEmptyComponent={
          !vm.loading && !vm.fetchError ? (
            <View style={localStyles.emptyWrap}>
              <Text style={styles.caption}>Sin movimientos recientes en esta sucursal.</Text>
            </View>
          ) : null
        }
        ListFooterComponent={
          vm.hasMore ? (
            <TouchableOpacity
              style={localStyles.loadMoreBtn}
              onPress={() => void vm.loadMore()}
              disabled={vm.loadingMore}
              accessibilityRole="button"
            >
              {vm.loadingMore ? (
                <ActivityIndicator color={ticketColors.gold} />
              ) : (
                <Text style={localStyles.loadMoreText}>Cargar más</Text>
              )}
            </TouchableOpacity>
          ) : null
        }
        ItemSeparatorComponent={null}
        contentContainerStyle={localStyles.listContent}
      />
    </View>
  );
}

const localStyles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 120,
  },
  offlineBanner: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  offlineText: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  errorBanner: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  listContent: {
    paddingBottom: spacing.md,
  },
  emptyWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
    gap: spacing.sm,
  },
  loadMoreBtn: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  loadMoreText: {
    ...typography.body,
    color: ticketColors.gold,
    fontWeight: '600',
  },
});
